import React from 'react'
import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { WaybackPreviewCarousel } from './result_components/WaybackPreviewCarousel'
import { MeetingsTimeline } from './result_components/MeetingsTimeline'

const defaultIcon = "data:image/svg+xml;utf8,\<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'>\<path d='M12 2a10 10 0 100 20 10 10 0 000-20zm0 17a1.25 1.25 0 110-2.5A1.25 1.25 0 0112 19zm1.2-5.8c-.8.5-1 1-1 1.8h-2c0-1.5.5-2.4 1.8-3.2.9-.6 1.2-1 1.2-1.8 0-1-0.8-1.8-2-1.8-1.1 0-2 .7-2 1.8H7c0-2.2 1.9-3.8 4.2-3.8 2.4 0 4.2 1.6 4.2 3.8 0 1.5-.7 2.4-2.2 3.2z'/>\</svg>"

const APP_ICONS: Record<string, string> = {
  search: "https://cdn.simpleicons.org/google",
  chrome: "https://cdn.simpleicons.org/googlechrome",
  gmail: "https://cdn.simpleicons.org/gmail",
  drive: "https://cdn.simpleicons.org/googledrive",
  docs: "https://cdn.simpleicons.org/googledocs",
  sheets: "https://cdn.simpleicons.org/googlesheets",
  slides: "https://cdn.simpleicons.org/googleslides",
  forms: "https://cdn.simpleicons.org/googleforms",
  meet: "https://cdn.simpleicons.org/googlemeet",
  chat: "https://cdn.simpleicons.org/googlechat",
  calendar: "https://cdn.simpleicons.org/googlecalendar",
  keep: "https://cdn.simpleicons.org/googlekeep",
  youtube: "https://cdn.simpleicons.org/youtube",
  youtubemusic: "https://cdn.simpleicons.org/youtubemusic",
  photos: "https://cdn.simpleicons.org/googlephotos",
  podcasts: "https://cdn.simpleicons.org/googlepodcasts",
  maps: "https://cdn.simpleicons.org/googlemaps",
  earth: "https://cdn.simpleicons.org/googleearth",
  googlecloud: "https://cdn.simpleicons.org/googlecloud",
  firebase: "https://cdn.simpleicons.org/firebase",
  analytics: "https://cdn.simpleicons.org/googleanalytics",
  ads: "https://cdn.simpleicons.org/googleads",
  adsense: "https://cdn.simpleicons.org/googleadsense",
  tagmanager: "https://cdn.simpleicons.org/googletagmanager",
  playconsole: "https://cdn.simpleicons.org/googleplay",
  android: "https://cdn.simpleicons.org/android",
  wearos: "https://cdn.simpleicons.org/wearos",
  chromecast: "https://cdn.simpleicons.org/googlecast",
  classroom: "https://cdn.simpleicons.org/googleclassroom",
  translate: "https://cdn.simpleicons.org/googletranslate",
  news: "https://cdn.simpleicons.org/googlenews",
  authenticator: "https://cdn.simpleicons.org/googleauthenticator",
  googleplay: "https://cdn.simpleicons.org/googleplay",
  spotify: "https://cdn.simpleicons.org/spotify",
  duolingo: "https://cdn.simpleicons.org/duolingo",
  twitter: "https://cdn.simpleicons.org/x",
  x: "https://cdn.simpleicons.org/x",
  default: defaultIcon,
}

interface GlookCommonProps {
  glookData: any
}

function getGlook(response: any) {
  if (response.apiDataEntities && Array.isArray(response.apiDataEntities)) {
    for (const entity of response.apiDataEntities) {
      if (entity.data && entity.data.emailSearchResultGlook) {
        return entity.data.emailSearchResultGlook
      }
    }
  }
  return null
}

const Glook: React.FC<GlookCommonProps> = ({ glookData }) => {
  const filteredData = getGlook(glookData)
  const highlights = filteredData?.wayback?.profile?.highlights
  const followers = filteredData?.wayback?.profile?.followers

  const dataObject = filteredData?.gaia?.details ? filteredData?.gaia?.details?.contributor : filteredData?.gaia?.contributor

  const contributor: any = {
    ...(dataObject?.points > 0 ? { points: dataObject.points } : {}),
    ...(dataObject?.level > 0 ? { level: dataObject.level } : {}),
    ...(dataObject?.local_guide ? { local_guide: true } : {}),

    ...(dataObject?.contributions?.reviews > 0 ? { reviews: dataObject.contributions.reviews } : {}),
    ...(dataObject?.contributions?.ratings > 0 ? { ratings: dataObject.contributions.ratings } : {}),
    ...(dataObject?.contributions?.photos > 0 ? { photos: dataObject.contributions.photos } : {}),
    ...(dataObject?.contributions?.videos > 0 ? { videos: dataObject.contributions.videos } : {}),
    ...(dataObject?.contributions?.captions > 0 ? { captions: dataObject.contributions.captions } : {}),
    ...(dataObject?.contributions?.answers > 0 ? { answers: dataObject.contributions.answers } : {}),
    ...(dataObject?.contributions?.edits > 0 ? { edits: dataObject.contributions.edits } : {}),
    ...(dataObject?.contributions?.reported_incorrect > 0 ? { reported_incorrect: dataObject.contributions.reported_incorrect } : {}),
    ...(dataObject?.contributions?.places_added > 0 ? { places_added: dataObject.contributions.places_added } : {}),
    ...(dataObject?.contributions?.roads_added > 0 ? { roads_added: dataObject.contributions.roads_added } : {}),
    ...(dataObject?.contributions?.facts_checked > 0 ? { facts_checked: dataObject.contributions.facts_checked } : {}),
    ...(dataObject?.contributions?.q_a > 0 ? { q_a: dataObject.contributions.q_a } : {}),
  }

  const gmail_applications =
    (filteredData?.email_info?.PROFILE_CONTAINER?.profile?.inAppReachability?.PROFILE?.apps ?? [])
      .map((item: string) => ({ name: item }))

  const additional_apps = (filteredData?.email_blkb?.results ?? []).map(({ name, url }: any) => ({
    name,
    url,
  }))

  const applications = [
    ...new Map(
      [...gmail_applications, ...additional_apps].map((app: any) => [
        app.name.toLowerCase(),
        app,
      ]),
    ).values(),
  ]

  const meetings: any[] = filteredData?.email_info?.PROFILE_CONTAINER?.calendar?.events?.items ?
    filteredData?.email_info?.PROFILE_CONTAINER?.calendar?.events?.items : []

  function removeDuplicatesByCreated<T extends { created?: string | null }>(arr: T[]): T[] {
    const seen = new Set<string>()

    return arr.filter((item) => {
      const key = item.created ?? ""

      if (!key) return true

      if (seen.has(key)) return false

      seen.add(key)
      return true
    })
  }

  const normalizeApp = (s: string) => s.trim().toLowerCase().replace(" ", "")

  const formatKey = (key: string) => key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())

  return (
    <>
      <Grid container sx={{ overflow: 'hidden' }}>
        {followers && (
          <Grid item xs={6}>
            <Grid container>
              <Grid item xs={4}>
                <Typography variant="text" fontSize={14} sx={{ wordBreak: 'break-all' }}>
                  Followers count:
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography
                  variant="bold"
                  sx={{ wordBreak: 'break-all', whiteSpace: 'pre-line', textAlign: 'center' }}
                >
                  {followers}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
                {contributor && Object.keys(contributor).length > 0 && (
          <>
            {Object.entries(contributor).map(([key, value]) => (
              <Grid item xs={6} key={key}>
                <Grid container>
                  <Grid item xs={4}>
                    <Typography variant="text" fontSize={14} sx={{ wordBreak: "break-all" }}>
                      {formatKey(key)}:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="bold"
                      sx={{ wordBreak: "break-all", whiteSpace: "pre-line", textAlign: "center" }}
                    >
                      {String(value)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </>
        )}
        {applications.length > 0 && (
          <Grid item xs={6}>
            <Grid container>
              <Grid item xs={4}>
                <Typography variant="text" fontSize={14} sx={{ wordBreak: "break-all" }}>
                  Applications:
                </Typography>
              </Grid>

              <Grid item xs={8}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  sx={{ whiteSpace: "pre-line", gap: 2 }}
                >
                  {applications.map((app: any) => {
                    const key = normalizeApp(app?.name)
                    const iconUrl = APP_ICONS[key] ? APP_ICONS[key] : APP_ICONS["default"]
                    return (
                      <Stack
                        key={app?.name}
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{ mr: 1 }}
                      >
                        {iconUrl && (
                          <Box
                            component="img"
                            src={iconUrl}
                            alt={app?.name}
                            sx={{ width: 18, height: 18 }}
                          />
                        )}
                        {app?.url ? (
                          <Link
                            href={app.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            color="inherit"
                            sx={{ wordBreak: "break-all" }}
                          >
                            <Typography variant="bold">
                              {app?.name}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography variant="bold" sx={{ wordBreak: "break-all" }}>
                            {app?.name}
                          </Typography>
                        )}
                      </Stack>
                    )
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      {highlights && (
        <Grid container sx={{ overflow: 'hidden', mt: 2 }}>
          <Grid item xs={12}>
            <WaybackPreviewCarousel items={highlights} />
          </Grid>
        </Grid>
      )}
      {meetings.length > 0 && (
        <Grid container sx={{ overflow: 'hidden', mt: 2 }}>
          <MeetingsTimeline meetings={removeDuplicatesByCreated(meetings ?? [])} />
        </Grid>
      )}
    </>
  )
}

export default Glook